import { useMutation, useQueryClient } from '@tanstack/react-query'
import { deleteJSON, patchJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { CustomField, CustomFieldResponse } from '@/lib/contacts/types'

export function useCustomFieldLifecycle() {
  const queryClient = useQueryClient()
  const setCustomFieldActive = useMutation({
    mutationFn: ({ field, active }: { field: CustomField; active: boolean }) =>
      patchJSON<CustomFieldResponse>(`/api/v1/custom_fields/${field.id}`, {
        custom_field: { active },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custom-fields'] })
    },
  })
  const deleteCustomField = useMutation({
    mutationFn: (field: CustomField) => deleteJSON(`/api/v1/custom_fields/${field.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custom-fields'] })
    },
  })

  function resetLifecycleErrors() {
    setCustomFieldActive.reset()
    deleteCustomField.reset()
  }

  function setActiveState(field: CustomField, active: boolean) {
    resetLifecycleErrors()
    setCustomFieldActive.mutate({ field, active })
  }

  function deleteField(field: CustomField) {
    if (!window.confirm(`Delete "${field.label}"?`)) return

    resetLifecycleErrors()
    deleteCustomField.mutate(field)
  }

  return {
    deleteField,
    error: lifecycleErrorMessage(setCustomFieldActive.error ?? deleteCustomField.error),
    setActiveState,
  }
}

function lifecycleErrorMessage(error: Error | null) {
  if (!error) return null

  return apiFieldMessages(error, 'base')[0] ?? error.message
}
